import { useMemo, useRef, useEffect } from "react";
import { useSubcellLoc, useSubcellSvg } from "../hooks.js";
import { HeaderWithModal } from "../utils.jsx";
import { all_species_by_sn } from "../rest.js";

const deeploc_threshold = {
    cytoplasm: '0.476',
    nucleus: '0.501',
    extracellular: '0.617',
    cell_membrane: '0.564',
    mitochondrion: '0.622',
    plastid: '0.640',
    endoplasmic_reticulum: '0.609',
    lysosome_vacuole: '0.585',
    golgi_apparatus: '0.649',
    perixosome: '0.736'
  };

// DeepLoc2 subcellular-localization term -> GO cellular-component accession(s).
// Used both to request the SwissBioPics cell image and to color it by score.
// Lysosome/Vacuole is one DeepLoc class that covers two organelles, so it maps
// to both GO:0005764 (lysosome) and GO:0005773 (vacuole).
const DEEPLOC_GO = {
    cytoplasm: '0005737',
    nucleus: '0005634',
    extracellular: '0005576',
    cell_membrane: '0005886',
    mitochondrion: '0005739',
    plastid: '0009536',
    endoplasmic_reticulum: '0005783',
    lysosome_vacuole: '0005764,0005773',
    golgi_apparatus: '0005794',
    perixosome: '0005777',
};

// CSS class SwissBioPics uses for a GO accession in its SVGs:
// "GO" + accession with the leading zeros stripped, e.g. 0005737 -> GO5737.
const goClass = go => `GO${Number(go)}`;

// DeepLoc 2.0 also predicts how a protein associates with the membrane
// (soluble / peripheral / transmembrane / lipid-anchored). Those scores come
// from the companion deeploc2_1 table.
const MEMBRANE_TERMS = [
    { key: 'soluble', label: 'Soluble' },
    { key: 'transmembrane', label: 'Transmembrane' },
    { key: 'peripheral', label: 'Peripheral' },
    { key: 'lipid_anchor', label: 'Lipid anchor' },
];

const membraneTerms = locs => MEMBRANE_TERMS
    .map(({ key, label }) => ({ key, label, score: Number(locs[key]) }))
    .filter(t => Number.isFinite(t.score));

const membranePrediction = locs => {
    const ranked = membraneTerms(locs).sort((a, b) => b.score - a.score);
    return ranked.length ? ranked[0].label : undefined;
};

// Pale (low score) -> deep (high score) blue heatmap stops, as [r,g,b].
const HEAT_FROM = [219, 234, 254];   // #dbeafe
const HEAT_TO = [30, 64, 175];       // #1e3a8a
const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const heatColor = score => {
    const t = Math.min(1, Math.max(0, score));
    const [r, g, b] = mix(HEAT_FROM, HEAT_TO, t);
    return `rgb(${r},${g},${b})`;
};

const SubLocdecoratedlist = (label, data, link=null, hover=null) =>
  <div className="flex">

      <div className="group relative inline-block m-1 w-1/6 pr-2 text-left border-r-2 border-slate-400 font-bold cursor-default">
          <div>{label}</div>

          {hover ? <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition bg-blue-400 text-white p-1 rounded absolute bottom-full mb-1 text-xs whitespace-nowrap">
          {hover}
          </div> : null}
      </div>

      <div className="m-auto ml-2">{data} {link}</div>

  </div>



const SubCellLoc = ({protein_seq_id, species_sn}) => {

    const {isReady, SubcellLoc} = useSubcellLoc(protein_seq_id)

    // SwissBioPics cell image: one SVG for the species' organism (NCBI taxid)
    // with every DeepLoc organelle visible, recoloured below by prediction score.
    const taxid = all_species_by_sn[species_sn] && all_species_by_sn[species_sn].taxid;
    const goIds = useMemo(() =>
        SubcellLoc && Object.keys(DEEPLOC_GO)
            .filter(k => k in SubcellLoc)
            .flatMap(k => DEEPLOC_GO[k].split(','))
            .join(',')
    , [SubcellLoc]);
    const {isReady: svgReady, svg} = useSubcellSvg(taxid, goIds);

    const svgRef = useRef(null);
    useEffect(() => {
        if (svgReady && svg && svgRef.current) {
            // The API response starts with an XML declaration that is not valid
            // HTML; strip it before injecting the SVG into the page.
            svgRef.current.innerHTML = svg.replace(/^<\?xml[\s\S]*?\?>/, '').trim();
        }
    }, [svgReady, svg]);

    // Score each DeepLoc term -> heatmap fill. Terms above their MCC threshold
    // are coloured by score (pale -> deep); the others keep the SVG's default
    // gray fill. The rules are scoped to this tile's image container.
    const heatCss = useMemo(() => {
        if (!SubcellLoc) return null;
        const base = [
            `#subcell-heat svg{width:100%;height:auto}`,
            `#subcell-heat .subcell_location {display:block}`,
        ].join('\n');
        const rules = Object.entries(DEEPLOC_GO)
            .filter(([k]) => k in SubcellLoc)
            .flatMap(([k, gos]) => {
                const score = SubcellLoc[k];
                if (typeof score !== 'number' || !(score >= 0) || score < Number(deeploc_threshold[k])) return [];
                const fill = heatColor(score);
                return gos.split(',').flatMap(go => {
                    const cls = goClass(go);
                    return [
                        `#subcell-heat svg .${cls} *:not(text){fill:${fill};opacity:0.9}`,
                        `#subcell-heat svg .mp_${cls} *:not(text){fill:${fill};opacity:0.9}`,
                        `#subcell-heat svg .part_${cls} *:not(text){fill:${fill};opacity:0.9}`,
                    ];
                });
            });
        return [base, ...rules].join('\n');
    }, [SubcellLoc]);

    const deeplocTable = (locs) => {
        const rows = Object.entries(locs)
            .filter(([k]) => !['protein_seq_id', 'signals', 'detected_localisation', 'peripheral', 'transmembrane', 'lipid_anchor', 'soluble'].includes(k))
            .map(([k, val]) => ({
                key: k,
                label: k.replace(/_/g, ' '),
                score: Number(val),
                threshold: Number(deeploc_threshold[k]),
            }))
            .sort((a, b) => b.score - a.score)
        return (
            <div className="overflow-hidden rounded border border-slate-100">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="border-b border-slate-200 text-left text-slate-500">
                            <th className="py-1.5 pl-2 pr-3 font-medium">Localization</th>
                            <th className="py-1.5 px-2 font-medium text-right">Score</th>
                            <th className="py-1.5 pr-2 pl-3 font-medium text-right">Threshold</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => {
                            const detected = r.score >= r.threshold;
                            return (
                                <tr key={r.key} className="border-b border-slate-100 last:border-0">
                                    <td className="py-1 pl-2 pr-3 capitalize">
                                        <span
                                            className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
                                            style={{background: detected ? heatColor(r.score) : '#cbd5e1'}}
                                            title={detected ? 'above threshold' : 'below threshold'}
                                        />
                                        <span className={detected ? "font-medium text-slate-800" : "text-slate-500"}>{r.label}</span>
                                    </td>
                                    <td className="py-1 px-2 text-right font-mono tabular-nums">{r.score.toFixed(3)}</td>
                                    <td className="py-1 pr-2 pl-3 text-right font-mono tabular-nums text-slate-400">{r.threshold.toFixed(3)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    const membraneTable = (locs) => {
        const rows = membraneTerms(locs).sort((a, b) => b.score - a.score);
        if (rows.length === 0) return null;
        const top = rows[0].score;
        return (
            <div className="overflow-hidden rounded border border-slate-100">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                        <tr className="border-b border-slate-200 text-left text-slate-500">
                            <th className="py-1.5 pl-2 pr-3 font-medium">State</th>
                            <th className="py-1.5 pr-2 pl-3 font-medium text-right">Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.key} className="border-b border-slate-100 last:border-0">
                                <td className="py-1 pl-2 pr-3">
                                    <span className={r.score === top ? "font-medium text-slate-800" : "text-slate-500"}>{r.label}</span>
                                    {r.score === top && <span className="ml-2 text-[11px] text-slate-400">predicted</span>}
                                </td>
                                <td className="py-1 pr-2 pl-3 text-right font-mono tabular-nums">{r.score.toFixed(3)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if(!isReady) {
        return <span>...loading</span>
    }

    if(!SubcellLoc) {
      return <div>no predicted localization for this sequence</div>
    }
    const membraneState = membranePrediction(SubcellLoc);
    const membrane = membraneTable(SubcellLoc);
    return (
      <div className="shadow-[0px_0px_6px_0px_rgba(0,0,0,0.2)] mt-3 mr-3 rounded p-3">
          <div className="border-b width-full border-slate-200">
            <HeaderWithModal title="Predicted Subcellular Localization by DeepLoc">
            <p>The subcellular localization of the protein was predicted using the DeepLoc 2.1 model.
            <a className="cursor-pointer hover:underline text-blue-600" href='/documentation'> See the documentation</a> for more details on the method.</p>
            <p>
                Localization:
                <ul>
                <li className='ml-4'>-Localizations for which DeepLoc 2.1 predicts a score higher than the respective threshold. If none of the predictions met the required threshold, the localization with the score closest to its threshold is given.</li>
                </ul>
            </p>
            <p>Signals: 
            <ul>
                <li className='ml-4'>-Sorting signals predicted by DeepLoc 2.1.</li>
                </ul>
            </p>
            <p>Membrane association: 
            <ul>
                <li className='ml-4'>-DeepLoc 2.1 also predicts how the protein is associated with membranes (soluble, peripheral, transmembrane or lipid-anchored); the state with the highest score is the predicted one.</li>
                </ul>
            </p>
            <p>Complete DeepLoc 2.1 output:
            <ul>
                <li className='ml-4'>-Prediction: Prediction score for each subcellular localization.</li>
                <li className='ml-4'>-Threshold(between parenthesis): Corresponding score threshold above which a prediction is deemed positive for each subcellular localization. The thresholds were provided by the developers and were calculated using Matthew’s Correlation Coefficient (MCC).</li>
                </ul>
            </p>
<p>Cell image: 
            <ul>
                <li className='ml-4'>-Cell drawing from <a className="cursor-pointer hover:underline text-blue-600" href="https://www.swissbiopics.org" target="_blank" rel="noopener noreferrer">SwissBioPics</a> (<a className="cursor-pointer hover:underline text-blue-600" href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer">CC BY 4.0</a>). Each detected localization is coloured by its DeepLoc 2.1 score (pale = close to the threshold, deep = confident); localizations below their threshold keep the default gray fill.</li>
                <li className='ml-4'>-Please cite: Le Mercier P, et al. SwissBioPics — an interactive library of cell images for the visualization of subcellular location data. Database (Oxford). 2022;2022:baac026. <a className="cursor-pointer hover:underline text-blue-600" href="https://doi.org/10.1093/database/baac026" target="_blank" rel="noopener noreferrer">doi:10.1093/database/baac026</a>.</li>
            </ul>
            </p>
          </HeaderWithModal>
          </div>

        <div className="mt-4 ml-0">
            {SubLocdecoratedlist('Localization', SubcellLoc.detected_localisation)}
            {SubLocdecoratedlist('Signals', SubcellLoc.signals)}
            {membraneState && SubLocdecoratedlist('Membrane', membraneState)}
        </div>

        <div className="mt-4 flex flex-row flex-wrap items-start gap-8">
            <div className="w-80 min-w-[260px] shrink-0">
                <div className="mb-1.5 text-sm font-medium text-slate-600">Complete DeepLoc 2.1 output</div>
                {deeplocTable(SubcellLoc)}
                {membrane && (
                    <div className="mt-3">
                        <div className="mb-1.5 text-sm font-medium text-slate-600">Membrane association</div>
                        {membrane}
                    </div>
                )}
            </div>
            {svgReady && svg && (
                <div className="min-w-[320px] flex-1">
                    <style>{heatCss}</style>
                    <div
                        id="subcell-heat"
                        ref={svgRef}
                        className="overflow-hidden rounded border border-slate-200 bg-white"
                        style={{maxWidth: 560}}
                    />
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                        <span>low</span>
                        <span
                            className="inline-block h-2.5 w-32 rounded"
                            style={{background: `linear-gradient(to right, rgb(${HEAT_FROM.join(',')}), rgb(${HEAT_TO.join(',')}))`}}
                        />
                        <span>high</span>
                        <span className="ml-2">prediction score</span>
                    </div>
                </div>
            )}
        </div>
      </div>
    );
}

export default SubCellLoc
